import { useMemo } from 'react';
import type { ReactNode } from 'react';

import { useSnackbar } from 'notistack';
import { Link as RouterLink } from 'react-router-dom';

import { definitionListSchema, TASK_STATUS_GROUP } from '@zercode/api-client';
import { useMe } from '@zercode/auth';
import { recordRouteFrom, TasksEnvironmentProvider, type TaskStatusOption, type TaskUserPickerProps } from '@zercode/forms';

import UserSelect from '../../../components/UserSelect';
import { authApi, definitionsClient, operationsClient } from '../../../lib/zercode';

// ==============================|| TASKS — THE ADMIN CONSOLE'S ENVIRONMENT ||============================== //
// What the shared task drawer (@zercode/forms) needs from this app. Mounted once, around the router.

/**
 * Deep-link registry (plan §7.3): which ADMIN route shows a linked record. The teacher app has its own table —
 * an appointment is /education/appointments/:id here and /appointments/:id there.
 */
export const adminTaskRecordRoute = recordRouteFrom({
  'operations:ticket': (id) => `/operations/tickets/${id}`,
  'engagement:event': (id) => `/education/events/${id}/edit`,
  'commerce:order': (id) => `/commerce/orders/${id}`,
  'identity:user': (id) => `/identity/users/${id}`,
  // CRM (docs/crm-module-plan.md §6): first contact, activity follow-up and stale-opportunity tasks link back here.
  'growth:lead': (id) => `/growth/crm/leads/${id}`,
  'growth:opportunity': (id) => `/growth/crm/opportunities/${id}`,
  'growth:account': (id) => `/growth/crm/accounts/${id}`,
  // APPT (docs/appointment-module-plan.md §8): Prepare / Record-outcome tasks link back to the appointment.
  'engagement:appointment': (id) => `/education/appointments/${id}`,
  // SALES (docs/sales-module-plan.md §3): the follow-up after a quote is sent links back to the quote.
  'commerce:quote': (id) => `/commerce/quotes/${id}`
});

function AdminUserPicker(props: TaskUserPickerProps) {
  return <UserSelect {...props} helperText="" />;
}

function useMyUserId(): string | null {
  return useMe(authApi).data?.id ?? null;
}

/** The same fetch as lib/useDefinitions' useDefinitionOptions — and the same cache key, so one entry serves both. */
async function fetchTaskStatuses(): Promise<TaskStatusOption[]> {
  const { data } = await definitionsClient.get<unknown>(`/api/v1/definitions/groups/${TASK_STATUS_GROUP}/items`, {
    query: { page: 1, pageSize: 100 }
  });
  return definitionListSchema
    .parse(data)
    .items.filter((d) => d.isActive)
    .sort((a, b) => a.orderNo - b.orderNo)
    .map((d) => ({ id: d.id, code: d.code, name: d.name, color: d.color, isDefault: d.isDefault }));
}

export default function AdminTasksEnvironment({ children }: { children: ReactNode }) {
  const { enqueueSnackbar } = useSnackbar();
  const value = useMemo(
    () => ({
      client: operationsClient,
      useMyUserId,
      recordRoute: adminTaskRecordRoute,
      linkComponent: RouterLink,
      UserPicker: AdminUserPicker,
      fetchStatuses: fetchTaskStatuses,
      notify: (message: string, variant: 'success' | 'error') => enqueueSnackbar(message, { variant })
    }),
    [enqueueSnackbar]
  );
  return <TasksEnvironmentProvider value={value}>{children}</TasksEnvironmentProvider>;
}
